import React, { useState, useEffect } from 'react';
import { Mail, Save, Eye, EyeOff, Info } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import useAuthStore from '../../context/authStore';
import { useLoader } from '../../components/shared/LoaderContext';

export default function AdminEmailConfigPage() {
  const { user } = useAuthStore();
  const { showLoader, hideLoader } = useLoader();
  const [config, setConfig] = useState({
    gmailId: '', gmailAppPassword: '',
    senderName: 'Bombay Psychiatric Society', isActive: true
  });
  const [testTo, setTestTo] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    api.get('/admin/email-config').then((res) => {
      if (res.data.config) setConfig((prev) => ({ ...prev, ...res.data.config, gmailAppPassword: '' }));
      setTestTo(user?.email || '');
    }).finally(() => setFetching(false));
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!config.gmailAppPassword) { toast.error('Please enter the Gmail App Password'); return; }
    const interval = showLoader('Saving email configuration...');
    try {
      await api.post('/admin/email-config', config);
      hideLoader(interval);
      toast.success('Email configuration saved successfully!');
    } catch (err) {
      hideLoader(interval);
      toast.error(err.response?.data?.message || 'Failed to save configuration');
    }
  };

  const handleTest = async () => {
    const interval = showLoader('Sending test email...');
    try {
      await api.post('/admin/email-config/test', { testTo });
      hideLoader(interval);
      toast.success(`Test email sent to ${testTo}`);
    } catch (err) {
      hideLoader(interval);
      toast.error(err.response?.data?.message || 'Test email failed. Check your configuration.');
    }
  };

  if (fetching) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h2>Email Configuration</h2>
        <p>Configure the Gmail account used to send notifications to members</p>
      </div>

      <div style={{
        background: '#eff6ff', border: '1px solid #bfdbfe',
        borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: 24,
        display: 'flex', gap: 12
      }}>
        <Info size={18} style={{ color: '#1e40af', flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 13, color: '#1e40af', lineHeight: 1.7 }}>
          <strong>Gmail App Password Required:</strong> Go to Google Account → Security →
          2-Step Verification → App Passwords. Generate a new app password for "Mail".
          Your password is stored encrypted using AES encryption.
        </div>
      </div>

      <div className="grid-2" style={{ alignItems: 'flex-start' }}>
        <div className="card" style={{ padding: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <Mail size={20} style={{ color: 'var(--primary)' }} />
            <h3 style={{ fontSize: 16, color: 'var(--primary)' }}>Gmail Settings</h3>
          </div>
          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="form-group">
              <label className="form-label">Sender Name</label>
              <input className="form-input" value={config.senderName}
                onChange={(e) => setConfig({ ...config, senderName: e.target.value })}
                placeholder="e.g. Bombay Psychiatric Society" />
            </div>
            <div className="form-group">
              <label className="form-label">Gmail Address *</label>
              <input type="email" className="form-input" value={config.gmailId}
                onChange={(e) => setConfig({ ...config, gmailId: e.target.value })}
                placeholder="youremail@gmail.com" required />
            </div>
            <div className="form-group">
              <label className="form-label">Gmail App Password *</label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? 'text' : 'password'} className="form-input"
                  value={config.gmailAppPassword}
                  onChange={(e) => setConfig({ ...config, gmailAppPassword: e.target.value })}
                  placeholder="Enter new app password to update"
                  style={{ paddingRight: 44 }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)'
                }}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={config.isActive}
                  onChange={(e) => setConfig({ ...config, isActive: e.target.checked })}
                  style={{ width: 16, height: 16 }} />
                <span className="form-label" style={{ margin: 0 }}>Enable email notifications</span>
              </label>
            </div>
            <button type="submit" className="btn btn-primary" style={{ alignSelf: 'flex-start', padding: '10px 24px' }}>
              <Save size={15} /> Save Configuration
            </button>
          </form>
        </div>

        <div className="card" style={{ padding: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Mail size={18} style={{ color: 'var(--accent)' }} />
            <h3 style={{ fontSize: 16, color: 'var(--primary)' }}>Send Test Email</h3>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
            After saving your configuration, send a test email to verify it works correctly.
          </p>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label">Send Test To</label>
            <input type="email" className="form-input" value={testTo}
              onChange={(e) => setTestTo(e.target.value)} placeholder="test@email.com" />
          </div>
          <button onClick={handleTest} disabled={!testTo} className="btn"
            style={{ background: 'var(--accent)', color: 'white', padding: '10px 20px' }}>
            <Mail size={15} /> Send Test Email
          </button>
        </div>
      </div>
    </div>
  );
}
